import { useCallback, useState } from 'react';

export function useAttachmentPreviewHook({ fetchPreview }) {
  const [cache, setCache] = useState(() => ({}));
  const [openAttachmentId, setOpenAttachmentId] = useState(null);
  const [loadingAttachmentId, setLoadingAttachmentId] = useState(null);

  const onAttachmentPreviewRequest = useCallback(
    async (attachment) => {
      const id = attachment?.id ?? attachment?.attachmentId;
      if (!id) return;

      const cached = cache[id];
      if (cached?.url) {
        setOpenAttachmentId(id);
        return;
      }

      setLoadingAttachmentId(id);
      setCache((prev) => ({
        ...prev,
        [id]: { ...prev[id], loading: true, error: undefined },
      }));

      try {
        const { url, fileName, fileType } = await fetchPreview(attachment);
        setCache((prev) => ({
          ...prev,
          [id]: { url, fileName, fileType, loading: false },
        }));
        setOpenAttachmentId(id);
      } catch (err) {
        console.error('[useAttachmentPreviewHook] Failed to fetch preview:', err);
        setCache((prev) => ({
          ...prev,
          [id]: {
            ...prev[id],
            loading: false,
            error: err?.message || 'Failed to load attachment',
          },
        }));
      } finally {
        setLoadingAttachmentId(null);
      }
    },
    [cache, fetchPreview],
  );

  const onCloseAttachmentPreview = useCallback(() => {
    setOpenAttachmentId(null);
  }, []);

  const previewForModal =
    openAttachmentId && cache[openAttachmentId]?.url
      ? {
          url: cache[openAttachmentId].url,
          fileName: cache[openAttachmentId].fileName ?? '',
          fileType: cache[openAttachmentId].fileType ?? 'other',
        }
      : null;

  return {
    loadingAttachmentId,
    previewForModal,
    onAttachmentPreviewRequest,
    onCloseAttachmentPreview,
  };
}
